import React, { useState, useEffect, useRef } from "react";
import { Search, MapPin } from "lucide-react";
import { API_ENDPOINTS } from "../services/apiconfig";

export default function FindBranches() {
  const [district, setDistrict] = useState("");
  const [societyType, setSocietyType] = useState("");
  const [districts, setDistricts] = useState([]);
  const [societyTypes, setSocietyTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    fetchDistricts();
    fetchSocietyTypes();
    initializeMap();

    return () => {
      // Clear all markers first
      if (markersRef.current && markersRef.current.length > 0) {
        markersRef.current.forEach(marker => {
          try {
            marker.remove();
          } catch (e) {
            console.warn('Error removing marker:', e);
          }
        });
        markersRef.current = [];
      }
      
      // Remove map instance
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.off();
          mapInstanceRef.current.remove();
        } catch (e) {
          console.warn('Error removing map:', e);
        }
        mapInstanceRef.current = null;
      }
      
      // Clear the Leaflet container ID
      if (mapRef.current) {
        if (mapRef.current._leaflet_id) {
          mapRef.current._leaflet_id = undefined;
        }
        mapRef.current.innerHTML = '';
      }
    };
  }, []);

  useEffect(() => {
    if (locations.length > 0 && mapInstanceRef.current) {
      updateMapMarkers();
    }
  }, [locations]);

  const initializeMap = () => {
    if (typeof window !== 'undefined' && !mapInstanceRef.current) {
      // Load Leaflet CSS
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      // Load Leaflet JS
      if (!window.L) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => createMap();
        document.head.appendChild(script);
      } else {
        createMap();
      }
    }
  };

  const createMap = () => {
    if (mapRef.current && !mapInstanceRef.current && window.L) {
      // Remove any existing map instance from the container
      if (mapRef.current._leaflet_id) {
        mapRef.current._leaflet_id = undefined;
      }
      
      // Clear any existing content
      mapRef.current.innerHTML = '';
      
      try {
        const map = window.L.map(mapRef.current, {
          zoomControl: true,
          scrollWheelZoom: true
        }).setView([11.0168, 76.9558], 11);
        
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(map);

        mapInstanceRef.current = map;
      } catch (error) {
        console.error('Error creating map:', error);
        // Clear the ref so it can try again
        mapInstanceRef.current = null;
      }
    }
  };

  const updateMapMarkers = () => {
    if (!mapInstanceRef.current || !window.L || locations.length === 0) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    const validLocations = [];

    // Add new markers
    locations.forEach((location, index) => {
      const lat = parseFloat(location.latitude);
      const lng = parseFloat(location.longitude);

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        console.warn(`Invalid coordinates for location ${index}:`, location);
        return;
      }

      validLocations.push([lat, lng]);

      // Create custom icon with number
      const customIcon = window.L.divIcon({
        className: 'custom-marker',
        html: `<div style="background-color: #dc2626; color: white; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">${index + 1}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });

      const marker = window.L.marker([lat, lng], { icon: customIcon })
        .addTo(mapInstanceRef.current);

      // Add popup with click to open Google Maps
      marker.bindPopup(`
        <div style="min-width: 200px;">
          <strong style="font-size: 14px;">${location.society_name}</strong><br/>
          <span style="font-size: 12px; color: #666;">
            Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}<br/>
            Accuracy: ${location.accuracy}m
          </span><br/>
          <a href="https://www.google.com/maps/search/?api=1&query=${lat},${lng}" 
             target="_blank" 
             style="display: inline-block; margin-top: 8px; padding: 6px 12px; background-color: #4285F4; color: white; text-decoration: none; border-radius: 4px; font-size: 12px; font-weight: 500;">
            Open in Google Maps
          </a>
        </div>
      `);

      // Make marker clickable to open Google Maps
      marker.on('click', () => {
        window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
      });

      markersRef.current.push(marker);
    });

    // Fit map to show all markers
    if (validLocations.length > 0) {
      try {
        if (validLocations.length === 1) {
          // If only one location, center on it with fixed zoom
          mapInstanceRef.current.setView(validLocations[0], 13);
        } else {
          // Multiple locations, fit bounds
          const bounds = window.L.latLngBounds(validLocations);
          mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (error) {
        console.error('Error fitting bounds:', error);
        // Fallback to first location
        if (validLocations.length > 0) {
          mapInstanceRef.current.setView(validLocations[0], 11);
        }
      }
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DISTRICTS);
      const result = await response.json();
      if (result.status === "success") {
        setDistricts(result.data.data);
        if (result.data.data.length > 0) {
          setDistrict(result.data.data[0].id.toString());
        }
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchSocietyTypes = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.SOCIETY_TYPES);
      const result = await response.json();
      if (result.status === "success") {
        setSocietyTypes(result.data.data);
        if (result.data.data.length > 0) {
          setSocietyType(result.data.data[0].id.toString());
        }
      }
    } catch (error) {
      console.error("Error fetching society types:", error);
    }
  };

  const fetchLocations = async () => {
    if (!societyType || !district) {
      alert("Please select both Society Type and District");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.SOCIETY_LOCATIONS}?society_type=${societyType}&district=${district}`
      );
      const result = await response.json();
      
      if (result.status === "success" && result.data.records.length > 0) {
        setLocations(result.data.records);
      } else {
        setLocations([]);
        alert("No locations found for the selected filters");
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      alert("Error fetching locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-8" style={{ backgroundColor: '#e5e5e5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-6">
     <h2 className="text-3xl md:text-4xl font-bold text-black mb-3">
  Find Our Branches
</h2>
<div className="w-20 h-1 bg-black mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-[90%] mx-auto">
          <div className="lg:col-span-2">
            <div className="bg-white shadow-lg overflow-hidden border border-gray-300 relative z-0">
              <div 
                ref={mapRef} 
                style={{ height: '400px', width: '100%' }}
                className="relative z-0"
              ></div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white shadow-lg p-6 border border-gray-300 space-y-4">
              <div>
                <label className="block text-gray-800 font-semibold mb-2 text-base">
                  District
                </label>
                <select
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all duration-200 hover:border-gray-400"
                  disabled={loading}
                >
                  <option value="">Select District</option>
                  {districts.map((dist) => (
                    <option key={dist.id} value={dist.id}>
                      {dist.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-800 font-semibold mb-2 text-base">
                  Society Type
                </label>
                <select
                  value={societyType}
                  onChange={(e) => setSocietyType(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all duration-200 hover:border-gray-400"
                  disabled={loading}
                >
                  <option value="">Select Society Type</option>
                  {societyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-center pt-2">
                <button
                  onClick={fetchLocations}
                  disabled={loading || !district || !societyType}
                  className="bg-gray-800 hover:bg-black text-white font-semibold py-3 px-8 transition-colors duration-200 flex items-center justify-center gap-2 text-sm shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      LOADING...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      FILTER
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}